import RequestUtil from "../utils/RequestUtil";

import Requests from "./Requests";

const basePath = "/explore.personallists.v1/";

import { PersonalListAddItem } from "../utils/PersonListUtils";
import * as PriorityUtil from "../utils/PriorityUtil";

type AddItemBody = {
    items: PersonalListAddItem[];
};

const service = {
    getList(country: string, listid: string): Promise<any> {
        const url = basePath + "lists/" + RequestUtil.buildPath([country, listid]);
        return Requests.get(url, null, { timeout: 30000, headers: RequestUtil.makeCallTreeHeader() });
    },

    getRecentlyBought(country: string, storeId: string, userId: string): Promise<any> {
        return service.getDynamicList(country, storeId, userId, "recentlyBought");
    },

    getCIP(country: string, storeId: string, userId: string): Promise<any> {
        return service.getDynamicList(country, storeId, userId, "cip");
    },

    getDynamicList(country: string, storeId: string, userId: string, type: string): Promise<any> {
        const url =
            basePath + "dynamicLists/country/" + country + "/storeId/" + storeId + "/customerId/" + userId + "/" + type;
        return Requests.get(url, null, { timeout: 30000, headers: RequestUtil.makeCallTreeHeader() });
    },

    fetchAllPersonalLists(
        country: string,
        storeId: string,
        userId: string,
        isEmployee: boolean,
        scoringKey: string | null
    ): Promise<any> {
        const store = storeId ? storeId : "";
        const user = userId ? userId : "";
        const sortingQuery = scoringKey ? "?sorting=" + scoringKey : "";
        const userPath = isEmployee ? "lists/employeeByUser/country/" : "lists/users/country/";
        const url = basePath + userPath + country + "/storeId/" + store + "/customerId/" + user + sortingQuery;
        return Requests.get(url, null, {
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeaderWithPriority(PriorityUtil.HIGH)
        });
    },

    createList(
        country: string,
        body: {
            [x: string]: any;
        }
    ): Promise<any> {
        const url = basePath + "lists/" + country + "/create";
        return Requests.post(url + "/", body, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    setCustomerVisible(country: string, listId: string, customerVisible: boolean): Promise<any> {
        const url = `${basePath}lists/country/${country}/listId/${listId}`;
        return Requests.post(url, { visibleForCustomer: customerVisible });
    },

    editListMetadata(
        country: string,
        userId: string | undefined | null,
        listId: string,
        body: {
            [x: string]: any;
        },
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/country/" +
            country +
            "/customerId/" +
            (userId ? userId : "") +
            "/listId/" +
            listId +
            "/edit" +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.post(url, body, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    replaceItem(
        country: string,
        customerId: string | undefined | null,
        listId: string,
        body: {
            [x: string]: any;
        },
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId, "replaceItem"]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.put(url, body, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    lockList(
        country: string,
        customerId: string | undefined | null,
        listId: string,
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId, "readOnlyOn"]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.post(url, null, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    unlockList(
        country: string,
        customerId: string | undefined | null,
        listId: string,
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId, "readOnlyOff"]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.post(url, null, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    deleteList(
        country: string,
        customerId: string | undefined | null,
        listId: string,
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.delete(url, null, { timeout: 30000, headers: RequestUtil.makeCallTreeHeader() });
    },

    deleteItems(
        country: string,
        customerId: string | undefined | null,
        listId: string | undefined | null,
        removeItems: {
            [x: string]: any;
        },
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId, "item"]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.delete(url, removeItems, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    setBundle(
        country: string,
        customerId: string | undefined | null,
        listId: string | undefined | null,
        body: {
            [x: string]: any;
        },
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId, "setBundle"]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.put(url, body, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    addToList(
        country: string,
        customerId: string | undefined | null,
        listId: string,
        body: AddItemBody,
        contextStoreId: string | undefined | null
    ): Promise<any> {
        const url =
            basePath +
            "lists/" +
            RequestUtil.buildPath([country, customerId, listId, "item"]) +
            "?" +
            RequestUtil.buildQuery({ contextStoreId: contextStoreId });
        return Requests.put(url, body, {
            dataType: "json",
            timeout: 30000,
            headers: RequestUtil.makeCallTreeHeader()
        });
    },

    updateLastInteraction(country: string, customerId: string | undefined | null, listId: string): Promise<any> {
        const url = basePath + "lists/" + RequestUtil.buildPath([country, customerId, listId]) + "/interaction";
        return Requests.put(
            url,
            {},
            {
                dataType: "json",
                timeout: 30000,
                headers: RequestUtil.makeCallTreeHeader()
            }
        );
    },

    createExportCsvUrl(listId: string): string {
        return basePath + "export/" + listId + "/csv";
    }
};

export default service;

export const {
    getList,
    getRecentlyBought,
    getCIP,
    getDynamicList,
    fetchAllPersonalLists,
    createList,
    setCustomerVisible,
    editListMetadata,
    replaceItem,
    lockList,
    unlockList,
    deleteList,
    deleteItems,
    setBundle,
    addToList,
    updateLastInteraction,
    createExportCsvUrl
} = service;
